import React, {createContext, useContext, useState, useCallback} from 'react'
import {client} from '../../../utils/api-client'

const PaymentAuthContext = createContext(null)

// Returned by the service (403) when the link's order UUID no longer matches the
// order — i.e. the rep re-sent the link, which rotates the UUID (FULSAS-14437).
const LINK_REPLACED_API_MESSAGE = 'Token not valid for this order'
export const LINK_REPLACED_MESSAGE =
  'This payment link has been replaced. Please use the newest link you received by email or text message to complete your payment.'

export function PaymentAuthProvider({children, flowType = 'cc'}) {
  const [authToken, setAuthToken] = useState(null)
  const [isVerified, setIsVerified] = useState(false)
  const [maskedEmail, setMaskedEmail] = useState(null)
  const [maskedContact, setMaskedContact] = useState(null)
  const [channel, setChannel] = useState('email')
  const [mfaRequired, setMfaRequired] = useState(null)
  const [orderExists, setOrderExists] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [otpSent, setOtpSent] = useState(false)
  const [linkReplaced, setLinkReplaced] = useState(false)

  const resetAuth = useCallback(() => {
    setAuthToken(null)
    setIsVerified(false)
    setOtpSent(false)
    setError('Your session has expired. Please verify your identity again.')
  }, [])

  const checkStatus = useCallback(
    async orderUuid => {
      setIsLoading(true)
      setError(null)
      try {
        const result = await client(
          `payment-auth/status/${orderUuid}?flowType=${flowType}`,
        )
        setOrderExists(result.exists)
        if (result.exists) {
          setMaskedEmail(result.maskedEmail)
          setMaskedContact(result.maskedContact || result.maskedEmail)
          setChannel(result.channel || 'email')
          // React 17 does not auto-batch setState after await, so each call
          // renders separately. Set the token BEFORE mfaRequired so the render
          // that flips the gate to <PaymentInformation /> already sees authToken.
          if (result.token) {
            setAuthToken(result.token)
          }
          setMfaRequired(result.mfaRequired ?? false)
        } else {
          setMfaRequired(false)
        }
        return result
      } catch (err) {
        // Old link opened after the rep re-sent: show the banner, not a generic error.
        if (err.status === 410 && err.data?.code === 'LINK_REPLACED') {
          setLinkReplaced(true)
          setOrderExists(false)
          setMfaRequired(false)
          return {exists: false, replaced: true}
        }
        setError(err.message || 'Unable to verify order')
        setOrderExists(false)
        setMfaRequired(false)
        return {exists: false}
      } finally {
        setIsLoading(false)
      }
    },
    [flowType],
  )

  const sendOtp = useCallback(async orderUuid => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await client('payment-auth/send-otp', {
        data: {orderUuid},
      })
      setMaskedEmail(result.maskedEmail)
      setMaskedContact(result.maskedContact || result.maskedEmail)
      setChannel(result.channel || 'email')
      setOtpSent(true)
      return result
    } catch (err) {
      setError(err.message || 'Failed to send verification code')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const verifyOtp = useCallback(async (orderUuid, otp) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await client('payment-auth/verify-otp', {
        data: {orderUuid, otp},
      })
      setAuthToken(result.token)
      setIsVerified(true)
      return result
    } catch (err) {
      setError(err.message || 'Verification failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const value = {
    authToken,
    isVerified,
    mfaRequired,
    orderExists,
    maskedEmail,
    maskedContact,
    channel,
    isLoading,
    error,
    otpSent,
    linkReplaced,
    setLinkReplaced,
    flowType,
    checkStatus,
    sendOtp,
    verifyOtp,
    setError,
    resetAuth,
  }

  return (
    <PaymentAuthContext.Provider value={value}>
      {children}
    </PaymentAuthContext.Provider>
  )
}

export function usePaymentAuth() {
  const context = useContext(PaymentAuthContext)
  if (!context) {
    throw new Error('usePaymentAuth must be used within PaymentAuthProvider')
  }
  return context
}

export function usePaymentAuthClient() {
  const {authToken, resetAuth, setLinkReplaced} = usePaymentAuth()

  const paymentClient = useCallback(
    async (endpoint, options = {}) => {
      try {
        return await client(endpoint, {
          ...options,
          token: authToken,
          headers: {
            'app-type': 'client-payment-link',
            ...options.headers,
          },
        })
      } catch (err) {
        if (err.status === 401) {
          resetAuth()
        }
        if (err.status === 403 && err.message === LINK_REPLACED_API_MESSAGE) {
          // Surfaced as a persistent banner by the gate; callers skip their
          // raw-error toast when they see err.linkReplaced.
          setLinkReplaced(true)
          err.linkReplaced = true
          err.message = LINK_REPLACED_MESSAGE
        }
        throw err
      }
    },
    [authToken, resetAuth, setLinkReplaced],
  )

  return paymentClient
}
